import React, { createContext, useState, useEffect, useCallback } from "react";

import sk from '../locales/sk.json';
import en from '../locales/en.json';
import de from '../locales/de.json';
import { registerAndSyncPrimeReactLocale } from '../utils/primeReactI18n';
import { setUiTranslator } from '../utils/uiTranslate';
import {
  normalizeAppLocale,
  resolveUiLocale,
  setStoredUiLocaleForUser,
  removeLegacyGlobalLocaleKey,
} from '../utils/appUiLocale';

const translations = { sk, en, de, gb: en };

const USER_PREF_KEY = 'preferred_language_user';
const COMPANY_PREF_KEY = 'preferred_language_company';
const AUTH_LOCALE_USER_ID_KEY = 'auth_locale_user_id';

/**
 * Call after tokens belong to `user` (login verify or cold refresh).
 * Updates account LS keys from JWT only. Does not touch per-user UI locale keys (`app_ui_locale:*`).
 */
export const applyAuthenticatedUserLocalePreferences = (user) => {
  if (typeof window === 'undefined' || !window.localStorage || !user?.id) return;
  const newId = String(user.id);
  localStorage.setItem(AUTH_LOCALE_USER_ID_KEY, newId);

  const userPref = normalizeAppLocale(user.preferred_language);
  if (userPref) localStorage.setItem(USER_PREF_KEY, userPref);
  else localStorage.removeItem(USER_PREF_KEY);

  const companyPref = normalizeAppLocale(user.company_preferred_language);
  if (companyPref) localStorage.setItem(COMPANY_PREF_KEY, companyPref);
  else localStorage.removeItem(COMPANY_PREF_KEY);

  window.dispatchEvent(new Event('app-locale-preferences-updated'));
};

/**
 * Logout: clear account prefs and legacy global locale; keep per-user `app_ui_locale:*` cache.
 */
export const clearLanguagePreferencesOnLogout = () => {
  if (typeof window === 'undefined' || !window.localStorage) return;
  removeLegacyGlobalLocaleKey();
  localStorage.removeItem(USER_PREF_KEY);
  localStorage.removeItem(COMPANY_PREF_KEY);
  localStorage.removeItem(AUTH_LOCALE_USER_ID_KEY);
  window.dispatchEvent(new Event('app-locale-preferences-updated'));
};

export const LocaleContext = createContext({
  locale: 'sk',
  setLocale: () => {},
  t: (key) => key,
});

const interpolateParams = (template, params = {}) => {
  if (typeof template !== 'string') return template;
  if (!params || typeof params !== 'object') return template;
  return Object.entries(params).reduce((acc, [key, value]) => {
    const safeValue = value == null ? '' : String(value);
    return acc
      .replace(new RegExp(`\\{\\{\\s*${key}\\s*\\}\\}`, 'g'), safeValue)
      .replace(new RegExp(`:${key}\\b`, 'g'), safeValue);
  }, template);
};

export const LocaleProvider = ({ children, authUser }) => {
  const [locale, setLocaleState] = useState(() => resolveUiLocale(authUser ?? null));

  useEffect(() => {
    setLocaleState(resolveUiLocale(authUser ?? null));
  }, [
    authUser?.id,
    authUser?.preferred_language,
    authUser?.company_preferred_language,
  ]);

  const setLocale = useCallback((nextLocale) => {
    const normalized = normalizeAppLocale(nextLocale) || 'sk';
    const id = authUser?.id;
    if (id != null) {
      setStoredUiLocaleForUser(id, normalized);
    }
    setLocaleState(normalized);
    window.dispatchEvent(new Event('app-ui-locale-changed'));
  }, [authUser?.id]);

  useEffect(() => {
    const refreshUiFromStorage = () => {
      setLocaleState(resolveUiLocale(authUser ?? null));
    };
    window.addEventListener('storage', refreshUiFromStorage);
    window.addEventListener('app-ui-locale-changed', refreshUiFromStorage);
    return () => {
      window.removeEventListener('storage', refreshUiFromStorage);
      window.removeEventListener('app-ui-locale-changed', refreshUiFromStorage);
    };
  }, [authUser]);

  useEffect(() => {
    registerAndSyncPrimeReactLocale(locale);
  }, [locale]);

  const t = useCallback((key, params = null) => {
    if (!key) return "";
    const translationSet = translations[locale] || {};
    // Prefer active locale → Slovak baseline → raw key (avoid exposing missing keys when SK has copy).
    const base = translationSet[key] ?? translations.sk?.[key] ?? key;
    return interpolateParams(base, params);
  }, [locale]);

  setUiTranslator(t, locale);

  return (
    <LocaleContext.Provider value={{ locale, setLocale, t }}>
      {children}
    </LocaleContext.Provider>
  );
};
